import { AUTH_MESSAGES, type SecondFactorChallenge } from '@fos/shared/contracts/identity';
import { type FormEvent, useState } from 'react';
import { ApiError, appErrorCode, errorMessage } from '../../../lib/api';
import { resendSecondFactor, useVerifySecondFactor } from '../../../lib/auth';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { CodeInput } from '../../../ui/CodeInput';
import { Field } from '../../../ui/Field';
import { codeErrorMessage, ResendButton } from './StepUpCode';

/**
 * Krok logowania „Wpisz kod z maila” (Fala 7, docs/API_AUTH.md): 6 cyfr (wklejanie, autouzupełnianie
 * `one-time-code`, po ostatniej cyfrze potwierdza sam), „Wyślij ponownie” z odliczaniem, „Zaufaj temu urządzeniu przez
 * 30 dni” (domyślnie jak „Zapamiętaj mnie”). Wygasłe logowanie albo blokada konta: powrót do hasła z komunikatem.
 */
export function SecondFactorStep({
  challenge,
  onVerified,
  onRestart,
}: {
  challenge: SecondFactorChallenge;
  onVerified: () => void;
  /** wróć do hasła (z komunikatem albo bez) */
  onRestart: (message?: string) => void;
}) {
  const verify = useVerifySecondFactor();
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(challenge.trustDeviceDefault);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState(challenge.email);
  const [resendAt, setResendAt] = useState(() => new Date(challenge.resendAvailableAt).getTime());
  const [resending, setResending] = useState(false);

  const confirm = async (value: string) => {
    if (verify.isPending) return;
    if (!/^\d{6}$/.test(value)) {
      setError(AUTH_MESSAGES.codeFormat);
      return;
    }
    setError(null);
    setNotice(null);
    try {
      await verify.mutateAsync({ code: value, trustDevice: trust });
      onVerified();
    } catch (err) {
      setCode('');
      const appCode = appErrorCode(err);
      if (appCode === 'UNAUTHENTICATED') onRestart(AUTH_MESSAGES.loginExpired);
      else if (appCode === 'RATE_LIMITED') onRestart(errorMessage(err));
      else setError(codeErrorMessage(err));
    }
  };

  const resend = async () => {
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      const res = await resendSecondFactor();
      setEmail(res.email);
      setResendAt(new Date(res.resendAvailableAt).getTime());
      setCode('');
      setNotice('Wysłaliśmy nowy kod. Poprzedni już nie działa.');
    } catch (err) {
      if (appErrorCode(err) === 'UNAUTHENTICATED') onRestart(AUTH_MESSAGES.loginExpired);
      else {
        if (err instanceof ApiError && err.retryAfterSec !== undefined) {
          setResendAt(Date.now() + err.retryAfterSec * 1000);
        }
        setError(err instanceof ApiError && err.code === 'RATE_LIMITED' ? err.message : errorMessage(err));
      }
    } finally {
      setResending(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void confirm(code);
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Drugi krok logowania">
      <p className="text-body-s text-fg-secondary">
        Wysłaliśmy 6-cyfrowy kod na <span className="font-medium text-fg">{email}</span>. Kod jest ważny 10 minut.
      </p>
      {notice && !error && <FormMessage tone="success">{notice}</FormMessage>}
      <div className="flex flex-col gap-2">
        <Field label="Kod z maila" error={error}>
          <CodeInput
            value={code}
            onChange={setCode}
            onComplete={(value) => void confirm(value)}
            autoFocus
            disabled={verify.isPending}
          />
        </Field>
        <ResendButton availableAt={resendAt} onResend={() => void resend()} pending={resending} />
      </div>
      <Checkbox checked={trust} onCheckedChange={setTrust} label="Zaufaj temu urządzeniu przez 30 dni" />
      <Button type="submit" variant="primary" size="l" fullWidth loading={verify.isPending} className="mt-1">
        Potwierdź
      </Button>
      <button
        type="button"
        onClick={() => onRestart()}
        className="self-center rounded-xs text-caption font-medium text-fg-secondary hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
      >
        Zaloguj się innym kontem
      </button>
      <p className="text-caption text-fg-muted">
        Nie widzisz maila? Sprawdź spam. Jeśli to nie Ty logujesz się na to konto, zmień hasło.
      </p>
    </form>
  );
}
